import React, { useContext } from 'react';
import { Stack, Select, MenuItem, Button } from '@mui/material';
import { QuickSearch } from 'components/_atoms';
import PageToolbar from '_lex/_atoms/PageToolbar';
import { translate as t } from 'hooks/useTranslations';
import { AppContext } from 'views/App/context';
import { Vertical } from 'types/vertical';

type DraftManualsUrlParams = {
  search?: string | null;
  verticalId?: string | null;
  sortOrder?: 'TITLE_ASCENDING' | 'TITLE_DESCENDING';
  orgId?: string | null;
  jurisdictionId?: string | null;
};

type FilterOption = {
  value: string;
  label: string;
};

export interface DraftManualsPolicyPageToolbarProps {
  params: DraftManualsUrlParams;
  updateParams: (updatedParams: Partial<DraftManualsUrlParams>) => void;
  isFiltered: boolean;
  clearParams: () => void;
  verticalsData?: Vertical[];
  jurisdictionOptions: FilterOption[];
  organizationOptions: FilterOption[];
  onCreateGlobalManual: () => void;
  onCreateAgencySupplementalManual: () => void;
  onCreateAgencyManual: () => void;
  children?: React.ReactNode;
}

const DraftManualsPolicyPageToolbar: React.FC<DraftManualsPolicyPageToolbarProps> = ({
  params,
  updateParams,
  isFiltered,
  clearParams,
  verticalsData,
  jurisdictionOptions,
  organizationOptions,
  onCreateGlobalManual,
  onCreateAgencySupplementalManual,
  onCreateAgencyManual,
  children,
}) => {
  const {
    currentProfile,
    currentOrganizationId,
    isLexipolStaff,
    hasPermission,
    userOrganizationId,
  } = useContext(AppContext);

  // Only fetch organizations for risk pool users
  const isRiskPoolUser = currentProfile?.organization?.riskPoolEnabled;

  const showActionButtons = hasPermission('createAgencyManual', userOrganizationId || '');
  const showFilters = isLexipolStaff;
  const showCreateLexipolManualButton = isLexipolStaff && hasPermission('createLexipolManual', currentOrganizationId);
  const showToolbar =
    isFiltered || isRiskPoolUser || showFilters || showCreateLexipolManualButton || showActionButtons;

  if(!showToolbar) {
    return null;
  }

  return (
    <PageToolbar
      leftItems={
        <Stack
          direction='row'
          sx={{
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            '& > *:not(:last-child)': {
              marginRight: 3,
            },
            '& > *': {
              minWidth: 'fit-content',
            },
          }}
        >
          {isLexipolStaff && (
            <>
              <QuickSearch
                id='keyword_search'
                initialValue={params.search || ''}
                onChange={(val) => updateParams({ search: val })}
                placeholder={t('policy.draftManuals.search.placeholder')}
                sx={{ width: 220, minWidth: 220, maxWidth: 220 }}
              />
              <Select
                id='vertical'
                size='small'
                label={false}
                value={params.verticalId || ''}
                onChange={({ target }) => updateParams({ verticalId: target.value })}
                displayEmpty
                sx={{
                  width: 220,
                  minWidth: 220,
                  maxWidth: 220,
                  '& fieldset': {
                    top: 0,
                  },
                  '& legend': {
                    display: 'none',
                  },
                }}
                renderValue={(selected) => {
                  if (!selected) {
                    return 'Verticals: All';
                  }
                  const vertical = verticalsData?.find((v) => v.id === selected);
                  return 'Verticals: ' + (vertical?.name || selected);
                }}
              >
                <MenuItem
                  key={`vertical_all`}
                  value=''
                >
                  {t('common.all')}
                </MenuItem>
                {verticalsData &&
                  verticalsData.map((k) => (
                    <MenuItem
                      key={`vertical_${k.id}`}
                      value={k.id}
                    >
                      {k.name}
                    </MenuItem>
                  ))}
              </Select>
              <Select
                id='jurisdiction-filter'
                size='small'
                label={false}
                value={params.jurisdictionId || ''}
                onChange={({ target }) => updateParams({ jurisdictionId: target.value })}
                displayEmpty
                sx={{
                  width: 220,
                  minWidth: 220,
                  maxWidth: 220,
                  '& fieldset': {
                    top: 0,
                  },
                  '& legend': {
                    display: 'none',
                  },
                }}
                renderValue={(selected) => {
                  if (!selected) {
                    return t('policy.draftManuals.filters.searchByJurisdiction') + ': All';
                  }
                  const jurisdiction = jurisdictionOptions.find((j) => j.value === selected);
                  return t('policy.draftManuals.filters.searchByJurisdiction') + ': ' + (jurisdiction?.label || selected);
                }}
              >
                <MenuItem value="">{t('common.all')}</MenuItem>
                {jurisdictionOptions.map((jurisdiction) => (
                  <MenuItem key={jurisdiction.value} value={jurisdiction.value}>
                    {jurisdiction.label}
                  </MenuItem>
                ))}
              </Select>
            </>
          )}
          {isRiskPoolUser && (
            <>
              {organizationOptions.length > 0 && (
                <Select
                  id='organization-filter'
                  size='small'
                  label={false}
                  value={params.orgId || ''}
                  onChange={({ target }) => updateParams({ orgId: target.value })}
                  displayEmpty
                  sx={{
                    width: 220,
                    minWidth: 220,
                    maxWidth: 220,
                    '& fieldset': {
                      top: 0,
                    },
                    '& legend': {
                      display: 'none',
                    },
                  }}
                  renderValue={(selected) => {
                    if (!selected) {
                      return t('policy.draftManuals.filters.searchByOrganization') + ': All';
                    }
                    const org = organizationOptions.find((o) => o.value === selected);
                    return t('policy.draftManuals.filters.searchByOrganization') + ': ' + (org?.label || selected);
                  }}
                >
                  <MenuItem value="">{t('common.all')}</MenuItem>
                  {organizationOptions.map((org) => (
                    <MenuItem key={org.value} value={org.value}>
                      {org.label}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </>
          )}
          {isFiltered && (
            <Button
              color='neutral'
              onClick={clearParams}
              size='small'
              sx={{
                whiteSpace: 'nowrap',
                minWidth: '80px',
                px: 2,
              }}
            >
              {t('common.clearFilters')}
            </Button>
          )}
        </Stack>
      }
      rightItems={
        <Stack
          direction='row'
          spacing={2}
          sx={{ alignItems: 'center', display: 'flex' }}
        >
          {children}
          {isLexipolStaff && hasPermission('createLexipolManual', currentOrganizationId) && (
            <Button
              size='small'
              onClick={onCreateGlobalManual}
            >
              {t('draftManuals.createGlobalManual.button')}
            </Button>
          )}
          {hasPermission('createAgencyManual', userOrganizationId || '') &&
            currentOrganizationId !== 'lexipoladminstaffuuid' && (
              <Button
                size='small'
                onClick={onCreateAgencySupplementalManual}
              >
                {t('draftManuals.createAgencySupplementalManual.button')}
              </Button>
            )}
          {hasPermission('createAgencyManual', userOrganizationId || '') &&
            currentOrganizationId !== 'lexipoladminstaffuuid' && (
              <Button
                size='small'
                onClick={onCreateAgencyManual}
              >
                {t('draftManuals.createAgencyManual.button')}
              </Button>
            )}
        </Stack>
      }
    />
  );
};

export default DraftManualsPolicyPageToolbar;
